/**
 * Контроль підписання первинки у списках документів (0429, 2026-08-17):
 * колонка «Підпис» + напівпрозорий червоний фон непідписаного рядка.
 *
 * Використання на сторінці-списку:
 *   const sc = useSignControl('doc.goods_sales', orgId, rows);
 *   <DataTable columns={[...cols, ...sc.column]} rowBackground={sc.rowBackground} ... />
 *
 * Рядки, що приїхали з 1С (external_source), і черновики (posted=false)
 * не контролюються. Якщо опція вимкнена на орг — колонка не додається.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColDef, IDatasource, RowClassParams, RowStyle } from 'ag-grid-community';
import { signControl, type SignStatus } from '@/api/signControl';
import type { ColumnDef } from '@/shared/components/DataTable';
import { SignIcon } from '@/shared/components/StatusIcons';

interface RowLike {
  id: string;
  posted?: boolean;
  state?: string;          // документи зі state-machine (ТТН: POSTED)
  external_source?: string | null;
}

/** Чи підпадає рядок під контроль: проведений і не мігрований з 1С. */
const isControllable = (r: RowLike): boolean =>
  r.posted !== false && (r.state === undefined || r.state === 'POSTED')
  && !r.external_source;

const UNSIGNED_BG = 'rgba(220, 38, 38, 0.07)';

export function useSignControl<T extends RowLike>(
  documentType: string, organizationId: string | null | undefined, rows: T[],
): {
  column: ColumnDef<T>[];
  rowBackground: (row: T) => string | undefined;
  statusOf: (row: T) => SignStatus | null;
} {
  const ids = useMemo(
    () => rows.filter(isControllable).map((r) => r.id),
    [rows]);
  const q = useQuery({
    queryKey: ['sign-control-statuses', documentType, organizationId, ids],
    queryFn: () => signControl.statuses(organizationId!, documentType, ids),
    enabled: !!organizationId && ids.length > 0,
    staleTime: 30_000,
  });
  const active = !!q.data?.enabled && !!q.data?.controlled;
  const statuses = q.data?.statuses ?? {};

  const statusOf = (row: T): SignStatus | null => {
    if (!active || !isControllable(row)) return null;
    return statuses[row.id] ?? null;
  };

  const column = useMemo<ColumnDef<T>[]>(() => !active ? [] : [{
    key: 'sign_control', header: 'Підпис', width: 90,
    sortValue: (r: T) => statuses[r.id] ?? '',
    cell: (r: T) => <SignIcon status={statusOf(r)} />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }], [active, statuses]);

  const rowBackground = (row: T): string | undefined =>
    statusOf(row) === 'UNSIGNED' ? UNSIGNED_BG : undefined;

  return { column, rowBackground, statusOf };
}

/**
 * Той самий контроль для infinite ag-grid сторінок (goods_sales/receipts):
 * обгортає datasource — після завантаження блоку рядків батч-запитом
 * тягне статуси і пише row.__sign_status; колонка/фон читають з нього.
 *
 *   const sc = useSignControlInfinite('doc.goods_sales', orgId);
 *   const { datasource } = useInfiniteGridDatasource(...);
 *   <InfiniteDocGrid datasource={sc.wrap(datasource)}
 *     columnDefs={[...cols, ...sc.column]} getRowStyle={sc.getRowStyle} />
 */
export function useSignControlInfinite<T extends RowLike>(
  documentType: string, organizationId: string | null | undefined,
): {
  wrap: (ds: IDatasource) => IDatasource;
  column: ColDef<T>[];
  getRowStyle: (p: RowClassParams<T>) => RowStyle | undefined;
} {
  // Probe: чи контролюється цей тип для орг (порожній список ids).
  const probeQ = useQuery({
    queryKey: ['sign-control-probe', documentType, organizationId],
    queryFn: () => signControl.statuses(organizationId!, documentType, []),
    enabled: !!organizationId,
    staleTime: 60_000,
  });
  const active = !!probeQ.data?.enabled && !!probeQ.data?.controlled;

  const wrap = (ds: IDatasource): IDatasource => !active ? ds : {
    ...ds,
    getRows: (params) => ds.getRows({
      ...params,
      successCallback: (rows, lastRow) => {
        const need = (rows as RowLike[]).filter(isControllable)
          .map((r) => r.id);
        if (!need.length || !organizationId) {
          params.successCallback(rows, lastRow);
          return;
        }
        signControl.statuses(organizationId, documentType, need)
          .then((res) => {
            for (const r of rows as (RowLike & { __sign_status?: SignStatus })[]) {
              if (isControllable(r)) r.__sign_status = res.statuses[r.id];
            }
          })
          .catch(() => {})
          .finally(() => params.successCallback(rows, lastRow));
      },
    }),
  };

  const column = useMemo<ColDef<T>[]>(() => !active ? [] : [{
    colId: 'sign_control', headerName: 'Підпис', width: 90, sortable: false,
    valueGetter: () => '',
    cellRenderer: (p: { data?: T & { __sign_status?: SignStatus } }) =>
      p.data ? <SignIcon status={p.data.__sign_status} /> : '',
  }], [active]);

  const getRowStyle = (p: RowClassParams<T>): RowStyle | undefined =>
    (p.data as (T & { __sign_status?: SignStatus }) | undefined)
      ?.__sign_status === 'UNSIGNED'
      ? { background: UNSIGNED_BG } : undefined;

  return { wrap, column, getRowStyle };
}

/**
 * DataTable-варіант для списків БЕЗ фільтра організації (рядки різних org):
 * групує rows по organization_id і тягне статуси батчем per-org.
 *
 *   const sc = useSignControlMulti('doc.bank_payments', rows);
 *   <DataTable columns={[...cols, ...sc.column]} rowBackground={sc.rowBackground} />
 */
export function useSignControlMulti<T extends RowLike & { organization_id: string }>(
  documentType: string, rows: T[],
): {
  column: ColumnDef<T>[];
  rowBackground: (row: T) => string | undefined;
} {
  const groups = useMemo(() => {
    const m = new Map<string, string[]>();
    rows.filter(isControllable).forEach((r) => {
      m.set(r.organization_id, [...(m.get(r.organization_id) ?? []), r.id]);
    });
    return m;
  }, [rows]);
  const key = useMemo(
    () => Array.from(groups.entries()).map(([o, ids]) => `${o}:${ids.join(',')}`).join('|'),
    [groups]);
  const q = useQuery({
    queryKey: ['sign-control-statuses-multi', documentType, key],
    queryFn: async () => {
      const out: Record<string, SignStatus> = {};
      await Promise.all(Array.from(groups.entries()).map(async ([org, ids]) => {
        const res = await signControl.statuses(org, documentType, ids);
        if (res.enabled && res.controlled) Object.assign(out, res.statuses);
      }));
      return out;
    },
    enabled: groups.size > 0,
    staleTime: 30_000,
  });
  const statuses = q.data ?? {};
  const active = Object.keys(statuses).length > 0;
  const column = useMemo<ColumnDef<T>[]>(() => !active ? [] : [{
    key: 'sign_control', header: 'Підпис', width: 90,
    sortValue: (r: T) => statuses[r.id] ?? '',
    cell: (r: T) => <SignIcon status={statuses[r.id]} />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }], [active, q.data]);
  const rowBackground = (row: T): string | undefined =>
    statuses[row.id] === 'UNSIGNED' ? UNSIGNED_BG : undefined;
  return { column, rowBackground };
}
